import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { useScrollOffset } from "@src/appV2/lib/utils/useScrollOffset";
import { type RefObject, useRef } from "react";

import { APP_V2_USER_EVENTS, logEvent } from "../../../lib/analytics";
import { useLogEffect } from "../../../lib/analytics/useLogEffect";
import { OpenShiftCardContainer } from "../../Shift/Open/CardContainer";
import { ShiftListDateHeader } from "../../Shift/Open/ListDateHeader";
import type { OpenShift } from "../../Shift/Open/types";
import { convertShiftToSlotId } from "../../Shift/Open/useGetShiftsSlotV2";
import { useOpenShiftListDataContext } from "../../Shift/Open/useOpenShiftListDataContext";

interface TopPickedShiftsListProps {
  readonly shifts: OpenShift[];
  readonly scrollContainerRef: RefObject<HTMLDivElement>;
}

export function TopPickedShiftsList(props: TopPickedShiftsListProps) {
  const { shifts, scrollContainerRef } = props;
  const numberOfShifts = shifts.length;
  const headerRef = useRef<HTMLDivElement>(null);
  const { isStuck } = useScrollOffset(scrollContainerRef.current ?? undefined, headerRef);

  const {
    onClickOpenShift,
    workplacesMap,
    shiftsBlockingRequirementsContextMap,
    shiftsSlotsMap,
    friendsMap,
    recentColleaguesMap,
    shiftAuctionsMap,
  } = useOpenShiftListDataContext();

  useLogEffect(
    APP_V2_USER_EVENTS.FOR_YOU_SHIFTS_LIST_VIEWED,
    { selectedShiftIds: shifts.map((shift) => shift.id), numberOfShifts },
    { enabled: numberOfShifts > 0 }
  );

  if (numberOfShifts === 0) {
    return null;
  }

  return (
    <Stack>
      <Box
        ref={headerRef}
        sx={(theme) => ({
          position: "sticky",
          top: 0,
          zIndex: 1,
          paddingY: 5,
          backgroundColor: theme.palette.background.tertiary,
          boxShadow: `inset 0 -1px 0 0 ${
            isStuck ? (theme.palette.border?.primary ?? "transparent") : "transparent"
          }`,
          borderRadius: 0,
          transition: theme.transitions.create("box-shadow"),
        })}
      >
        <ShiftListDateHeader label="For You" totalShiftsCount={shifts.length} sx={{ px: 5 }} />
      </Box>
      {shifts.map((shift, index) => {
        const workplace = workplacesMap.get(shift.relationships.workplace.data.id);
        if (!isDefined(workplace)) {
          return null;
        }

        const shiftAuctionId = shift.relationships.shiftAuction?.data.id;
        const shiftAuction = shiftAuctionId ? shiftAuctionsMap.get(shiftAuctionId) : undefined;

        return (
          <Box key={shift.id} pt={index === 0 ? 0 : 5}>
            <OpenShiftCardContainer
              isForYouShift
              shift={shift}
              workplace={workplace}
              blockingRequirementsContext={
                shiftsBlockingRequirementsContextMap.get(shift.id) ?? undefined
              }
              workers={
                shiftsSlotsMap
                  .get(
                    convertShiftToSlotId({
                      start: shift.attributes.start,
                      end: shift.attributes.end,
                      facilityId: workplace.id,
                    })
                  )
                  ?.shiftAssignments.filter((assignment) => assignment.shiftId !== shift.id)
                  .map(({ worker }) => worker) ?? []
              }
              friendsMap={friendsMap}
              recentColleaguesMap={recentColleaguesMap}
              shiftAuction={shiftAuction}
              sx={{ mx: 5 }}
              onClick={({ offer }) => {
                logEvent(APP_V2_USER_EVENTS.FOR_YOU_SHIFTS_SHIFT_CLICKED, {
                  shiftId: shift.id,
                });
                onClickOpenShift({
                  shift,
                  workplace,
                  offer,
                  shiftAuction,
                  isForYouShift: true,
                });
              }}
            />
          </Box>
        );
      })}
    </Stack>
  );
}
